/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
/**
 * Home page constants
 * All hardcoded data for home page sections
 */
import { type TFunction } from 'i18next'

// Layout - Main base classes
export const MAIN_BASE_CLASSES = 'bg-background text-foreground w-full'

// Hero section - AI Applications (Left side)
export const AI_APPLICATIONS = [
  'LobeHub.Color',
  'Dify.Color',
  'OpenWebUI',
  'Cline',
] as const

// Hero section - AI Models (Right side)
export const AI_MODELS = [
  'Qwen.Color',
  'DeepSeek.Color',
  'Doubao.Color',
  'OpenAI',
  'Claude.Color',
  'Gemini.Color',
] as const

// Hero section - Gateway Features
export const GATEWAY_FEATURES = [
  'Cost Tracking',
  'Model Access',
  'Guardrails',
  'Observability',
  'Budgets',
  'Load Balancing',
  'Rate Limiting',
  'Token Mgmt',
  'Prompt Caching',
  'Pass-Through',
] as const

// Stats section - Default statistics
export const DEFAULT_STATS = [
  {
    value: '50',
    suffix: '+',
    description: 'upstream services integrated',
  },
  {
    value: '100',
    suffix: '+',
    description: 'model billing support',
  },
  {
    value: '50',
    suffix: '+',
    description: 'compatible API routes',
  },
  {
    value: '10',
    suffix: '+',
    description: 'scheduling controls',
  },
] as const

// Features section - Default features
export const DEFAULT_FEATURES = [
  {
    title: 'Lightning Fast',
    description:
      'Optimized network architecture ensures millisecond response times',
    iconName: 'Zap',
  },
  {
    title: 'Secure & Reliable',
    description:
      'Enterprise-grade security with comprehensive permission management',
    iconName: 'Shield',
  },
  {
    title: 'Global Coverage',
    description: 'Multi-region deployment for stable global access',
    iconName: 'Globe',
  },
  {
    title: 'Developer Friendly',
    description: 'Compatible API routes for common AI application workflows',
    iconName: 'Code',
  },
  {
    title: 'High Performance',
    description: 'Support for high concurrency with automatic load balancing',
    iconName: 'Gauge',
  },
  {
    title: 'Transparent Billing',
    description: 'Pay-as-you-go with real-time usage monitoring',
    iconName: 'DollarSign',
  },
  {
    title: 'Team Collaboration',
    description: 'Multi-user management with flexible permission allocation',
    iconName: 'Users',
  },
  {
    title: 'Open Source',
    description: 'Community driven, self-hosted, and extensible',
    iconName: 'HeartHandshake',
  },
] as const

// ─────────────────────────────────────────────────────────────────────────────
// MODEL SHOWCASE SECTION
// Edit the values below to update the marketplace section on the homepage.
// Model names and counts are literal strings (not i18n keys).
// Section/tab headings use t() — add new keys to src/i18n/locales/{en,zh}.json.
// ─────────────────────────────────────────────────────────────────────────────

export type ModelTab = 'text' | 'image' | 'video'

export interface ProviderCard {
  /** Two-letter initials shown in the badge */
  initials: string
  /** Display name of the provider */
  name: string
  /** Model count label — e.g. "18 个模型" / "18 models" (zh / en) */
  countZh: string
  countEn: string
  /** Badge background color */
  badgeBg: string
  /** Badge text/icon color */
  badgeColor: string
}

export interface ModelCard {
  /** Two-letter initials for the badge */
  initials: string
  /** Provider abbreviation color pair (same as ProviderCard) */
  badgeBg: string
  badgeColor: string
  /** Model name chip label */
  name: string
  /** Provider display name */
  provider: string
  /** Price label supplied by the live pricing catalog. */
  price: string
  /** Capability tag — e.g. "Chat", "Image", "Video" */
  tag: string
}

/**
 * MODEL_SHOWCASE
 *
 * Single source of truth for the model marketplace section.
 * To update any content, edit this object — the component reads from here.
 */
export const MODEL_SHOWCASE = {
  // ── PROVIDER GRID ──────────────────────────────────────────────────────────
  // Six main provider cards shown in the grid.
  providers: [
    {
      initials: 'OA',
      name: 'OpenAI',
      countZh: '18 个模型',
      countEn: '18 models',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
    },
    {
      initials: 'An',
      name: 'Anthropic',
      countZh: '4 个模型',
      countEn: '4 models',
      badgeBg: '#fdf0ea',
      badgeColor: '#d25f1e',
    },
    {
      initials: 'Go',
      name: 'Google',
      countZh: '56 个模型', // changed from design's 34
      countEn: '56 models',
      badgeBg: '#eaf0fd',
      badgeColor: '#4285f4',
    },
    {
      initials: 'Gk',
      name: 'Grok', // renamed from DeepSeek per product spec
      countZh: '8 个模型', // changed from design's 3
      countEn: '8 models',
      badgeBg: '#f0eafd',
      badgeColor: '#7c3aed',
    },
    {
      initials: 'BD',
      name: '字节跳动',
      countZh: '10 个模型',
      countEn: '10 models',
      badgeBg: '#fff3ea',
      badgeColor: '#ff6c00',
    },
    {
      initials: 'Al',
      name: '阿里巴巴',
      countZh: '5 个模型',
      countEn: '5 models',
      badgeBg: '#fff0ea',
      badgeColor: '#e85d00',
    },
  ] satisfies ProviderCard[],

  // ── MORE PROVIDERS TAG ROW ─────────────────────────────────────────────────
  // Small tag chips shown below the provider grid.
  moreProviders: [
    'Vertex',
    '亚马逊', // Amazon
    'Flow',
    'Black Forest Labs (FLUX)',
  ],

  // ── SECTION HEADLINE ───────────────────────────────────────────────────────
  // Shown above the tab row. Update both zh and en.
  headlineZh: '统一访问 200+ AI 模型',
  headlineEn: 'Access 200+ AI models',

  // ── TEXT MODELS TAB ────────────────────────────────────────────────────────
  textModels: [
    {
      initials: 'OA',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
      name: 'gpt-5.5', // renamed from gpt-5.4
      provider: 'OpenAI',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'An',
      badgeBg: '#fdf0ea',
      badgeColor: '#d25f1e',
      name: 'claude-opus-4-8',
      provider: 'Anthropic',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'Go',
      badgeBg: '#eaf0fd',
      badgeColor: '#4285f4',
      name: 'gemini-3.5-flash', // renamed from gemini-2.5-pro
      provider: 'Google',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'DS',
      badgeBg: '#f0eafd',
      badgeColor: '#7c3aed',
      name: 'deepseek-v4-pro',
      provider: 'DeepSeek',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'An',
      badgeBg: '#fdf0ea',
      badgeColor: '#d25f1e',
      name: 'claude-sonnet-4-6',
      provider: 'Anthropic',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'OA',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
      name: 'gpt-5.5',
      provider: 'OpenAI',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'Al',
      badgeBg: '#fff0ea',
      badgeColor: '#e85d00',
      name: 'qwen3.7-plus', // renamed from qwen3.5-plus
      provider: '阿里巴巴',
      price: '—',
      tag: 'Chat',
    },
    {
      initials: 'DS',
      badgeBg: '#f0eafd',
      badgeColor: '#7c3aed',
      name: 'deepseek-v3.2',
      provider: 'DeepSeek',
      price: '—',
      tag: 'Chat',
    },
  ] satisfies ModelCard[],

  // ── IMAGE MODELS TAB ───────────────────────────────────────────────────────
  // Exactly 5 image models per product spec.
  imageModels: [
    {
      initials: 'OA',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
      name: 'GPT-Image 2', // NOTE: user said "gpt-5.4 → image 2" — interpreted as "GPT-Image 2" — FLAG FOR CONFIRMATION
      provider: 'OpenAI',
      price: '—',
      tag: 'Image',
    },
    {
      initials: 'NB',
      badgeBg: '#fff9e6',
      badgeColor: '#b45309',
      name: 'Nano Banana 2',
      provider: 'NanoBanana',
      price: '—',
      tag: 'Image',
    },
    {
      initials: 'Go',
      badgeBg: '#eaf0fd',
      badgeColor: '#4285f4',
      name: 'Gemini-3.5-flash',
      provider: 'Google',
      price: '—',
      tag: 'Image',
    },
    {
      initials: 'Gk',
      badgeBg: '#f0eafd',
      badgeColor: '#7c3aed',
      name: 'Grok-Image-1.5',
      provider: 'Grok',
      price: '—',
      tag: 'Image',
    },
    {
      initials: 'FL',
      badgeBg: '#e8f4fd',
      badgeColor: '#1a56db',
      name: 'Flux',
      provider: 'Black Forest Labs',
      price: '—',
      tag: 'Image',
    },
  ] satisfies ModelCard[],

  // ── VIDEO MODELS TAB ───────────────────────────────────────────────────────
  // Original design video models (sora-2, veo-3.1, kling-v3, hailuo-video,
  // vidu-q2 — sourced from noyapi-homepage-styled.html MP_NAMES/MP_CATS where
  // cat=='video') kept, with "Omni flash" appended per product instruction.
  videoModels: [
    {
      initials: 'OA',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
      name: 'sora-2',
      provider: 'OpenAI',
      price: '—',
      tag: 'Video',
    },
    {
      initials: 'Go',
      badgeBg: '#eaf0fd',
      badgeColor: '#4285f4',
      name: 'veo-3.1',
      provider: 'Google',
      price: '—',
      tag: 'Video',
    },
    {
      initials: 'KL',
      badgeBg: '#f0eafd',
      badgeColor: '#7c3aed',
      name: 'kling-v3',
      provider: 'Kuaishou',
      price: '—',
      tag: 'Video',
    },
    {
      initials: 'HL',
      badgeBg: '#fff3ea',
      badgeColor: '#ff6c00',
      name: 'hailuo-video',
      provider: 'MiniMax',
      price: '—',
      tag: 'Video',
    },
    {
      initials: 'VD',
      badgeBg: '#fff0ea',
      badgeColor: '#e85d00',
      name: 'vidu-q2',
      provider: 'Vidu',
      price: '—',
      tag: 'Video',
    },
    {
      initials: 'OA',
      badgeBg: '#e8f5f0',
      badgeColor: '#10a37f',
      name: 'Omni flash',
      provider: 'OpenAI',
      price: '—',
      tag: 'Video',
    },
  ] satisfies ModelCard[],
} as const

export function getGatewayFeatures(t: TFunction) {
  return GATEWAY_FEATURES.map((feature) => t(feature))
}

export function getDefaultStats(t: TFunction) {
  return DEFAULT_STATS.map((stat) => ({
    ...stat,
    description: stat.description ? t(stat.description) : undefined,
  }))
}

export function getDefaultFeatures(t: TFunction) {
  return DEFAULT_FEATURES.map((feature) => ({
    ...feature,
    title: t(feature.title),
    description: t(feature.description),
  }))
}
